
// Utility functions for currency formatting and handling

export const getCurrencySymbol = (currencyCode: string): string => {
  const currencySymbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
    // Add more currencies as needed
  };
  
  return currencySymbols[currencyCode] || currencyCode;
};

export const formatCurrency = (
  amount: number | string,
  currencyCode: string = 'USD'
): string => {
  // Convert amount to number if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle NaN
  if (isNaN(numericAmount)) {
    return `${getCurrencySymbol(currencyCode)}0.00`;
  }
  
  // Special case for JPY which doesn't use decimals
  if (currencyCode === 'JPY') {
    return `${getCurrencySymbol(currencyCode)}${Math.round(numericAmount).toLocaleString()}`;
  }
  
  // Format with 2 decimal places for other currencies
  return `${getCurrencySymbol(currencyCode)}${numericAmount.toFixed(2)}`;
};

export const getCurrencyByCode = (currencyCode: string): string => {
  const currencies: Record<string, string> = {
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
    JPY: 'Japanese Yen',
    CAD: 'Canadian Dollar',
    AUD: 'Australian Dollar',
    // Add more as needed
  };
  
  return currencies[currencyCode] || currencyCode;
};

// Currency mapping by country
export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  'United States': 'USD',
  'Canada': 'CAD',
  'United Kingdom': 'GBP',
  'Australia': 'AUD',
  'Japan': 'JPY',
  // Euro countries
  'Austria': 'EUR',
  'Belgium': 'EUR',
  'Cyprus': 'EUR',
  'Estonia': 'EUR',
  'Finland': 'EUR',
  'France': 'EUR',
  'Germany': 'EUR',
  'Greece': 'EUR',
  'Ireland': 'EUR',
  'Italy': 'EUR',
  'Latvia': 'EUR',
  'Lithuania': 'EUR',
  'Luxembourg': 'EUR',
  'Malta': 'EUR',
  'Netherlands': 'EUR',
  'Portugal': 'EUR',
  'Slovakia': 'EUR',
  'Slovenia': 'EUR',
  'Spain': 'EUR',
  // Add more as needed
};

// New function to get default currency for a country
export const getDefaultCurrencyForCountry = (country: string | null): string => {
  if (!country) return 'USD'; // Default to USD if no country provided
  
  // Try to find an exact match first
  if (COUNTRY_TO_CURRENCY[country]) {
    return COUNTRY_TO_CURRENCY[country];
  }
  
  // Check for Euro zone countries
  const euroCountries = [
    'Austria', 'Belgium', 'Cyprus', 'Estonia', 'Finland', 'France', 
    'Germany', 'Greece', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 
    'Luxembourg', 'Malta', 'Netherlands', 'Portugal', 'Slovakia', 
    'Slovenia', 'Spain'
  ];
  
  if (euroCountries.includes(country)) {
    return 'EUR';
  }
  
  // Try to find partial matches
  for (const knownCountry in COUNTRY_TO_CURRENCY) {
    if (country.toLowerCase().includes(knownCountry.toLowerCase()) || 
        knownCountry.toLowerCase().includes(country.toLowerCase())) {
      return COUNTRY_TO_CURRENCY[knownCountry];
    }
  }
  
  // Default fallback
  return 'USD';
};
